"use client"

import { useRef } from "react"
import { motion, useInView } from "framer-motion"
import {
  GlobeAsiaAustraliaIcon,
  MagnifyingGlassIcon,
  DocumentCheckIcon,
  WrenchScrewdriverIcon,
  CubeTransparentIcon,
  SparklesIcon, // ✅ Replaced TreeIcon with SparklesIcon
} from "@heroicons/react/24/outline"

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
}

const miningProcess = [
  {
    id: 1,
    title: "Site Selection",
    description:
      "Pinpointing emerald-rich zones in the Panjshir Valley using geological and historical data to ensure high-yield investments.",
    icon: <GlobeAsiaAustraliaIcon className="h-8 w-8" />,
  },
  {
    id: 2,
    title: "Exploration",
    description:
      "Mapping emerald veins in the Hindu Kush with cutting-edge surveying tools, laying the foundation for profitable mining ventures.",
    icon: <MagnifyingGlassIcon className="h-8 w-8" />,
  },
  {
    id: 3,
    title: "Planning & Permits",
    description:
      "Crafting sustainable plans and securing permits for Panjshir Valley mining, ensuring compliance and investor confidence.",
    icon: <DocumentCheckIcon className="h-8 w-8" />,
  },
  {
    id: 4,
    title: "Extraction",
    description:
      "Extracting emeralds from Panjshir's rugged terrain with precision, delivering premium gems for market-leading returns.",
    icon: <WrenchScrewdriverIcon className="h-8 w-8" />,
  },
  {
    id: 5,
    title: "Processing",
    description:
      "Sorting and assessing Panjshir emeralds for their signature vivid green quality, enhancing value for Dubai Panjshir EmeraldVault stakeholders.",
    icon: <CubeTransparentIcon className="h-8 w-8" />,
  },
  {
    id: 6,
    title: "Valley Rehabilitation",
    description:
      "Restoring Panjshir mining sites to preserve the valley's natural beauty, aligning with ethical investment principles.",
    icon: <SparklesIcon className="h-8 w-8" />, // ✅ Updated
  },
]

export default function MiningProcessSection() {
  const processRef = useRef(null)
  const processInView = useInView(processRef, { once: true, amount: 0.1 })

  return (
    <section
      id="process"
      className="py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-br from-emerald-950 via-emerald-900 to-green-800 text-white"
    >
      <motion.div
        ref={processRef}
        initial="hidden"
        animate={processInView ? "visible" : "hidden"}
        variants={fadeIn}
        className="container mx-auto max-w-7xl"
      >
        <div className="text-center mb-12 md:mb-16">
          <motion.div
            variants={fadeIn}
            className="inline-block px-4 py-1 bg-emerald-800/70 backdrop-blur-sm rounded-full text-emerald-100 font-sans text-sm tracking-wider uppercase mb-4 shadow-lg shadow-emerald-900/30"
          >
            Our Workflow
          </motion.div>
          <motion.h2
            variants={fadeIn}
            className="text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-emerald-200 to-green-200 bg-clip-text text-transparent"
          >
            Dubai Panjshir EmeraldVault Mining Process
          </motion.h2>
          <motion.p variants={fadeIn} className="max-w-2xl mx-auto text-emerald-100 text-lg">
            A meticulous process to mine Panjshir emeralds, ensuring quality, ecological harmony, and exceptional value
            for stakeholders.
          </motion.p>
        </div>

        {/* Desktop Timeline (hidden on mobile) */}
        <div className="hidden md:block relative">
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-emerald-700/70 rounded-full"></div>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={processInView ? "visible" : "hidden"}
            className="space-y-16"
          >
            {miningProcess.map((step, index) => (
              <motion.div
                key={step.id}
                variants={fadeIn}
                className={`relative flex items-center ${index % 2 === 0 ? "flex-row" : "flex-row-reverse"}`}
              >
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center z-10 shadow-lg shadow-emerald-900/50 border-2 border-emerald-400/30">
                  <span className="text-white font-bold">{step.id}</span>
                </div>
                <div className={`w-1/2 ${index % 2 === 0 ? "pr-16 text-right" : "pl-16 text-left"}`}>
                  <div className="p-6 bg-emerald-800/40 backdrop-blur-sm rounded-xl border border-emerald-600/30 shadow-xl shadow-emerald-900/20 hover:shadow-emerald-700/30 transition-all duration-300 hover:-translate-y-1 group">
                    <h3 className="text-2xl font-bold mb-3 group-hover:text-emerald-300 transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-emerald-100">{step.description}</p>
                  </div>
                </div>
                <div className={`w-1/2 ${index % 2 === 0 ? "pl-16 flex justify-start" : "pr-16 flex justify-end"}`}>
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center shadow-lg shadow-emerald-900/30 border border-emerald-500/30 text-emerald-100">
                    {step.icon}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Mobile Process Cards (visible only on mobile) */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={processInView ? "visible" : "hidden"}
          className="md:hidden space-y-6"
        >
          {miningProcess.map((step) => (
            <motion.div
              key={step.id}
              variants={fadeIn}
              className="bg-emerald-800/40 backdrop-blur-sm rounded-xl border border-emerald-600/30 p-5 shadow-lg shadow-emerald-900/20"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center mr-4 shadow-md shadow-emerald-900/30 border border-emerald-500/30">
                  <span className="text-white font-bold">{step.id}</span>
                </div>
                <h3 className="text-xl font-bold text-emerald-100">{step.title}</h3>
              </div>
              <div className="flex items-start">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center mr-4 shadow-md shadow-emerald-900/30 border border-emerald-500/30 text-emerald-100 flex-shrink-0">
                  {step.icon}
                </div>
                <p className="text-emerald-100 text-sm">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  )
}
